import { isNullish } from '@nx/stdlib';

import type { DeploymentDetailsResponse, DeploymentResponse, License, Neo4jConfigResponse } from '../api-types';
import type { Deployment, DeploymentDetails, License as LicenseType, Neo4jConfig } from '../types/topology';
import { transformToken } from './tokens';

export function transformGetDeploymentsResponse(res: DeploymentResponse[]): Deployment[] {
  return res.map(
    (deployment): Deployment => ({
      deploymentId: deployment.deployment_id,
      name: deployment.name,
      createdBy: deployment.created_by,
      status: deployment.status,
      connectionUrl: deployment.connection_url,
    }),
  );
}

function transformLicense(license?: License): LicenseType | undefined {
  if (isNullish(license)) {
    return undefined;
  }
  return {
    type: license.type,
    state: license.state,
    daysLeftOnTrial: license.days_left_on_trial,
    totalTrialDays: license.total_trial_days,
  };
}

function getServerDisplayName(serverId: string, serverName: string, serverAddress: string): string {
  if (serverName === '' || serverName === serverId) {
    return serverAddress;
  }
  return serverName;
}

export function transformGetDeploymentDetailsResponse(deployment: DeploymentDetailsResponse): DeploymentDetails {
  return {
    deploymentId: deployment.deployment_id,
    name: deployment.name,
    createdBy: deployment.created_by,
    status: deployment.status,
    connectionUrl: deployment.connection_url,
    token: deployment.token ? transformToken(deployment.token) : undefined,
    dbms: deployment.dbms
      ? {
          dbmsId: deployment.dbms.dbms_id,
          version: deployment.dbms.version,
          status: deployment.dbms.status,
          edition: deployment.dbms.edition,
          packaging: deployment.dbms.packaging,
          license: transformLicense(deployment.dbms.license),
          bloomLicense: transformLicense(deployment.dbms.bloom_license),
          gdsLicense: transformLicense(deployment.dbms.gds_license),
          metricCollectionEnabled: deployment.dbms.metric_collection_enabled,
          databases: deployment.dbms.databases.map((database) => ({
            databaseId: database.database_id,
            name: database.name,
          })),
          servers: deployment.dbms.servers.map((server) => ({
            address: server.address,
            name: server.name,
            displayName: getServerDisplayName(server.server_id, server.name, server.address),
            databaseInstances: server.database_instances.map((instance) => ({
              serverId: server.server_id,
              serverName: server.name,
              databaseName: instance.database_name,
              access: instance.access,
              aliases: instance.aliases,
              creationTime: instance.creation_time,
              currentPrimariesCount: instance.current_primaries_count,
              currentSecondariesCount: instance.current_secondaries_count,
              currentStatus: instance.current_status,
              statusMessage: instance.status_message,
              databaseId: instance.database_id,
              default: instance.default,
              lastStartTime: instance.last_start_time,
              requestedPrimariesCount: instance.requested_primaries_count,
              requestedSecondariesCount: instance.requested_secondaries_count,
              requestedStatus: instance.requested_status,
              role: instance.role,
              store: instance.store,
              writer: instance.writer,
              lastCommittedTxn: instance.last_committed_txn,
              replicationLag: instance.replication_lag,
              graphShards: instance.graph_shards,
              propertyShards: instance.property_shards,
              type: instance.type,
            })),
            health: server.health,
            state: server.state,
            lastPing: server.last_ping,
            serverId: server.server_id,
            modeConstraint: server.mode_constraint,
            version: server.version,
            status: server.status,
            license: transformLicense(server.license),
            bloomLicense: transformLicense(server.bloom_license),
            gdsLicense: transformLicense(server.gds_license),
            pluginVersion: server.plugin_version,
          })),
        }
      : undefined,
  };
}

export function transformGetDeploymentConfigsResponse(configs: Neo4jConfigResponse[]): Neo4jConfig[] {
  return configs.map((config) => {
    return {
      serverId: config.server_id,
      config: config.neo4j_config,
    };
  });
}
